// NAV-001 -- Separar Clientes y Marketing en la navegación principal.
//
// Lógica pura (sin React) de qué aparece en el dropdown "Marketing" y cuándo
// se considera "activo" -- extraída de Header.tsx para que Desktop
// (HorizontalNav) y Mobile (MobileNav) consuman EXACTAMENTE la misma fuente
// de verdad (nunca pueden divergir en estructura) y para que sea testeable
// con vitest puro (este proyecto corre tests en environment:'node', sin
// jsdom/React Testing Library -- mismo patrón que campaignDispatchPolicy.ts,
// campaignWebhookStatus.ts, etc: la decisión vive en una función pura, el
// componente solo la renderiza).
//
// No cambia ninguna regla de permisos: reusa exactamente los mismos módulos
// ('crm', 'loyalty', 'appointments') que ya usaba el dropdown "Clientes"
// antes de esta separación.

export interface MarketingNavPermissions {
  crm: boolean;
  loyalty: boolean;
  /** Necesario solo para "Clases grupales" (mismo gate que antes: appointments + industria). */
  appointments: boolean;
}

export interface MarketingNavItem {
  id: 'campanas' | 'loyalty' | 'memberships' | 'group-classes';
  label: string;
  href: string;
  /** react-router state -- Campañas lo usa para abrir /crm directo en su
   *  pestaña "Campañas" (mismo mecanismo que CampaignEditorPage.goBackToCampaigns
   *  y CRMCampaignsTab ya usan para volver a /crm con una pestaña elegida). */
  state?: Record<string, unknown>;
}

/** Rutas que cuentan como "dentro de Marketing" para el estado activo del nav. */
export const MARKETING_ACTIVE_HREFS = ['/crm', '/loyalty', '/memberships', '/group-classes'] as const;

export const CLIENTS_HREF = '/clients';
export const CLIENTS_LABEL = 'Clientes';
export const MARKETING_LABEL = 'Marketing';

/**
 * Arma la lista de items del dropdown Marketing, en el orden pedido:
 * Campañas, Programa de Puntos VIP, Membresías, y (solo fitness/spa)
 * Clases grupales. Cualquier item cuyo permiso falte simplemente no aparece
 * -- nunca se agrega uno "deshabilitado visible", se omite del todo (mismo
 * criterio que el dropdown Clientes tenía antes).
 */
export function buildMarketingNavItems(
  perms: MarketingNavPermissions,
  industryType: string | null,
): MarketingNavItem[] {
  const items: MarketingNavItem[] = [];

  if (perms.crm) {
    items.push({ id: 'campanas', label: 'Campañas', href: '/crm', state: { tab: 'campanas' } });
  }
  if (perms.loyalty) {
    items.push({ id: 'loyalty', label: 'Programa de Puntos VIP', href: '/loyalty' });
    items.push({ id: 'memberships', label: 'Membresías', href: '/memberships' });
  }
  if (perms.appointments && (industryType === 'fitness' || industryType === 'spa')) {
    items.push({ id: 'group-classes', label: 'Clases grupales', href: '/group-classes' });
  }

  return items;
}

/**
 * Marketing solo se muestra si tiene al menos un item real -- nunca un
 * dropdown vacío. "Clases grupales" por sí solo NUNCA basta para mostrar
 * Marketing (mismo criterio que el dropdown Clientes tenía antes, donde
 * "Clases grupales" era siempre un extra dentro del grupo, nunca lo que
 * decidía si el grupo existía).
 */
export function shouldShowMarketingNav(perms: { crm: boolean; loyalty: boolean }): boolean {
  return perms.crm || perms.loyalty;
}

/** true si el pathname actual cae dentro de cualquier ruta de Marketing. */
export function isMarketingActive(pathname: string): boolean {
  return MARKETING_ACTIVE_HREFS.some((href) => pathname.startsWith(href));
}

/** true si el pathname actual cae dentro de Clientes (ahora un link directo, sin submenu). */
export function isClientsActive(pathname: string): boolean {
  return pathname.startsWith(CLIENTS_HREF);
}
